import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, CheckCircle, Clock, Activity } from 'lucide-react';
import { dashboardService } from '@/features/dashboard/services/dashboard.service';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TasksAnalyticsTab } from '@/features/dashboard/components/tasks/TasksAnalyticsTab';
import { UsersTab } from '@/features/dashboard/components/users/UsersTab';

export const Route = createFileRoute('/_authenticated/admin-dashboard')({
  beforeLoad: () => {
    // Verificar permissão antes de carregar
    const token = localStorage.getItem('token');
    if (!token) {
      throw redirect({
        to: '/login',
      });
    }

    // A verificação de permissão será feita no componente
  },
  component: AdminDashboardPage,
});

function AdminDashboardPage() {
  const { hasPermission } = usePermissions();
  const allowed = hasPermission(Permission.DASHBOARD_ADMIN) || !!hasPermission?.(Permission.ASSETS_EDIT);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      return await dashboardService.getStats();
    },
    enabled: allowed,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });

  const statsData = {
    totalAssets: stats?.totalAssets ?? 0,
    availableAssets: stats?.availableAssets ?? 0,
    pendingRequests: stats?.pendingRequests ?? 0,
    assetsInUse: stats?.assetsInUse ?? 0,
    users: stats?.users || {
      totalUsers: 0,
      activeUsers: 0,
      inactiveUsers: 0,
      usersByTeam: [],
      usersWithoutTeam: 0,
    },
  };

  // Verificação adicional no componente
  if (!allowed) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard Administrativa</h1>
        <p className="text-muted-foreground">Visão geral do sistema</p>
      </div>

      <Tabs defaultValue="tasks" className="space-y-4">
        <TabsList>
          <TabsTrigger value="equipments">Equipamentos</TabsTrigger>
          <TabsTrigger value="users">Usuários</TabsTrigger>
          <TabsTrigger value="tasks">Tarefas</TabsTrigger>
        </TabsList>

        <TabsContent value="equipments" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Equipamentos</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-2xl font-bold">...</div>
                ) : (
                  <div className="text-2xl font-bold">{statsData.totalAssets}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Disponíveis</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-2xl font-bold">...</div>
                ) : (
                  <div className="text-2xl font-bold">{statsData.availableAssets}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Solicitações Pendentes</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-2xl font-bold">...</div>
                ) : (
                  <div className="text-2xl font-bold">{statsData.pendingRequests}</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Em Uso</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-2xl font-bold">...</div>
                ) : (
                  <div className="text-2xl font-bold">{statsData.assetsInUse}</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UsersTab />
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <TasksAnalyticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}



